import {
  ColumnVO,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from "@ag-grid-community/core";
import { ColumnParamData } from "gen/model/columnParamData";
import { ServerSideTableRequestDataRequest } from "gen/model/serverSideTableRequestDataRequest";
import { ServerSideTableResponseAPIData } from "gen/model/serverSideTableResponseAPIData";
import { mapValues } from "lodash-es";
import { useMemo } from "react";
import { translateColumns } from "shared/components/table/data/internal/ag-props/DataTableAgPropsColumns";
import { COLUMN_ID_AUTO_COLUMN } from "shared/components/table/data/internal/DataTableConstants";
import { valueForCellKeyCreator } from "shared/components/table/data/internal/DataTableInternalUtils";
import { useToast } from "shared/components/toast/useToast";
import { keysToSnakeCase } from "shared/utils/ObjectUtils";

export const useDataTableServerSideSource = (
  getTableData: (
    params: ServerSideTableRequestDataRequest,
  ) => Promise<ServerSideTableResponseAPIData>,
): IServerSideDatasource => {
  const { showErrorToast } = useToast();
  return useMemo<IServerSideDatasource>(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        if (params.request.pivotMode && !params.request.pivotCols.length) {
          params.success({ rowData: [] });
          params.api.setPivotResultColumns([]);
          return;
        }
        try {
          const resp = await getTableData({
            start_row: params.request.startRow,
            end_row: params.request.endRow,
            sort_model: params.request.sortModel
              .filter((s) => s.colId !== COLUMN_ID_AUTO_COLUMN)
              .map(keysToSnakeCase),
            filter_model: mapValues(
              params.request.filterModel,
              keysToSnakeCase,
            ),
            row_group_cols: params.request.rowGroupCols.map(
              translateRequestColumn,
            ),
            group_keys: params.request.groupKeys.map((k) =>
              typeof k === "object" ? valueForCellKeyCreator(k) : k,
            ),
            ...(params.request.pivotMode
              ? {
                  pivot_mode: true,
                  pivot_cols: params.request.pivotCols.map(
                    translateRequestColumn,
                  ),
                  value_cols: params.request.valueCols.map(
                    translateRequestColumn,
                  ),
                }
              : {}),
          });
          const colDefs = translateColumns(resp.table_data, params.context);
          if (params.request.pivotMode) {
            params.api.setPivotResultColumns(colDefs);
          }
          params.success({
            rowData: resp.table_data.rows,
          });
          params.api.refreshHeader();
        } catch (e) {
          showErrorToast(e);
          params.fail();
        }
      },
    }),
    [getTableData, showErrorToast],
  );
};

const translateRequestColumn = (c: ColumnVO): ColumnParamData => ({
  id: c.id,
  agg_function: c.aggFunc === "dataTableCellSumAggFunc" ? "sum" : c.aggFunc,
});
